import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import { ShippingAddressInput, Callback } from 'lib/types'
import { Slice, ShippingAddressSlice } from './types'
import { setCart } from '../cart'
import { PWAThunk } from 'lib/core/data'

export const initialState: Slice = {}

interface ActionData {
  cartId: string
  uuid: string
}

const getSlice = (slice: ShippingAddressSlice, uuid?: string): ShippingAddressSlice => {
  return slice ?? ({
    uuid,
    isFetching: false,
    isSuccess: false,
    isFailure: false
  })
}

const slice = createSlice({
  name: 'quote/setShippingAddress',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId], uuid)
      slice.uuid = uuid
      slice.isFetching = true
      state[cartId] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId])
      if (uuid === slice.uuid) {
        slice.isFetching = false
        slice.isSuccess = true
        slice.isFailure = false
      }
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId])
      if (uuid === slice.uuid) {
        slice.isFetching = false
        slice.isSuccess = false
        slice.isFailure = true
      }
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export interface SetShippingAddressParams { // TODO: this interface is used in many places. Find a way to re-use
  shippingAddress: ShippingAddressInput
  [key: string]: any
}

export function setShippingAddress (cartId: string, actionParams: SetShippingAddressParams, callback?: Callback): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    const uuid = uuidv4()
    const { shippingAddress, ...restInputParams } = actionParams
    dispatch(request({ cartId, uuid }))
    try {
      const response = await magentoClient.getClient().quote.setShippingAddresses({
        cartId,
        shippingAddress,
        ...restInputParams
      })

      const { data } = response
      const { setShippingAddressesOnCart } = data
      const { cart } = setShippingAddressesOnCart

      if (cart) {
        dispatch(setCart({
          cartId,
          data: cart,
          merge: true
        }))
        dispatch(success({ cartId, uuid }))
        callback?.onSuccess?.()
      } else {
        dispatch(failure({ cartId, uuid }))
        callback?.onFailure?.({
          error: new Error(`[Shipping Address] Unexpected payload: ${JSON.stringify(response)}`),
          extraData: {
            cartId,
            shippingAddress,
            ...restInputParams
          }
        })
      }

      return response
    } catch (e) {
      dispatch(failure({ cartId, uuid }))
      callback?.onFailure?.({
        error: new Error(`[Shipping Address] ${e.message}`),
        extraData: {
          cartId,
          shippingAddress,
          ...restInputParams
        }
      })
    }
  }
}
